import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, FormattedNumber} from "react-intl";

import {styled} from "@mui/material/styles";

import InfoTooltipIcon from "components/CustomTooltip/InfoTooltipIcon";
import {
    black500,
    green100,
    green50,
    green700,
    greyishBlue100,
    greyishBlue200,
    greyishBlue500,
    greyishBlue600,
    red100,
    red50,
    red700
} from "assets/jss/main";

CatalogSummaryCard.propTypes = {
    labelId: PropTypes.string.isRequired,
    tooltipId: PropTypes.string.isRequired,
    // null while loading: the card keeps its shape and shows a dash rather than 0
    value: PropTypes.number,
    // signed %, null when there is nothing to compare against — which is NOT 0%
    variation: PropTypes.number,
    // overrides the "vs <period>" caption (the disabled card lists its causes instead)
    footerId: PropTypes.string,
    periodLabelId: PropTypes.string.isRequired,
    // absent for the cards whose list filters are not decided yet: the card then renders
    // as plain text rather than looking actionable
    onClick: PropTypes.func,
};

// A button when it filters the list, a plain div otherwise: only the cards whose list
// equivalent is settled are actionable, and a card that does nothing must not look
// clickable. Rendering a real <button> keeps it keyboard-reachable for free.
const Card = styled('div', {shouldForwardProp: (prop) => 'clickable' !== prop})(({theme, clickable}) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '12px',
    flex: '1 1 0',
    // Without it a long label would widen the card instead of letting all five share
    // the row evenly.
    minWidth: 0,
    padding: '20px',
    backgroundColor: '#fff',
    border: `1px solid ${greyishBlue100}`,
    borderRadius: '16px',
    // Reset the button defaults so both variants render identically.
    textAlign: 'left',
    font: 'inherit',
    // On mobile the cards keep their width and the row scrolls horizontally instead
    // of squeezing all of them into the viewport.
    [theme.breakpoints.down('md')]: {
        flex: '0 0 auto',
        minWidth: '240px'
    },
    ...(clickable
        ? {
            cursor: 'pointer',
            '&:hover': {
                borderColor: greyishBlue200,
                boxShadow: '0px 4px 22px 0px rgba(0, 0, 0, 0.04)'
            }
        }
        : {})
}));

const Header = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    width: '100%'
});

const Label = styled('span')({
    color: greyishBlue600,
    fontSize: '16px',
    fontWeight: 600,
    // The tooltip icon is pushed to the far right of the header.
    flex: '1 1 0',
    minWidth: 0
});

const Body = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    width: '100%'
});

const ValueRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
});

const Value = styled('span')({
    color: black500,
    fontSize: '32px',
    fontWeight: 700,
    whiteSpace: 'nowrap'
});

// Green when the count went up, red when it went down. The DS uses the 50/100/700 triplet
// of each hue for background, border and text.
const Variation = styled('span', {shouldForwardProp: (prop) => 'positive' !== prop})(({positive}) => ({
    display: 'inline-flex',
    alignItems: 'center',
    height: '24px',
    padding: '0 8px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    backgroundColor: positive ? green50 : red50,
    border: `1px solid ${positive ? green100 : red100}`,
    color: positive ? green700 : red700
}));

const Footer = styled('span')({
    color: greyishBlue500,
    fontSize: '12px'
});

function CatalogSummaryCard({labelId, tooltipId, value, variation, footerId, periodLabelId, onClick}) {
    const hasValue = null !== value && undefined !== value;
    const hasVariation = null !== variation && undefined !== variation;

    return (
        <Card
            as={onClick ? 'button' : 'div'}
            type={onClick ? 'button' : undefined}
            clickable={Boolean(onClick)}
            onClick={onClick}
        >
            <Header>
                <Label><FormattedMessage id={labelId}/></Label>
                <InfoTooltipIcon titleId={tooltipId} size={20} placement="bottom-end"/>
            </Header>
            <Body>
                <ValueRow>
                    <Value>
                        {hasValue ? <FormattedNumber value={value}/> : '—'}
                    </Value>
                    {hasVariation && (
                        <Variation positive={variation >= 0}>
                            {variation >= 0 ? '+ ' : '- '}
                            <FormattedNumber value={Math.abs(variation)}/>
                            %
                        </Variation>
                    )}
                </ValueRow>
                <Footer>
                    {hasValue && !hasVariation
                        ? <FormattedMessage id={"retailer.product.catalogSummary.versus.noData"}/>
                        : footerId
                            ? <FormattedMessage id={footerId}/>
                            : <FormattedMessage
                                id={"retailer.product.catalogSummary.versus"}
                                values={{period: <FormattedMessage id={periodLabelId}/>}}
                            />}
                </Footer>
            </Body>
        </Card>
    );
}

export default CatalogSummaryCard;
